import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import Link from 'components/atoms/Link';
import { Title } from 'components/atoms/Title';
import { SizesEnum } from 'components/atoms/Title/Title.enum';
import CustomLoader from 'components/molecules/CustomLoader.component';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import {
  useGroupSubscriptionPlans,
  useRedirectToCustomerBillingPortal,
  useSubscriptionPlan,
} from 'hooks/Subscriptions';
import useFeatures from 'hooks/useFeatures';
import _get from 'lodash/get';
import { useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import type { SubscriptionPlan } from 'types/subscriptions.types';
import type { IUser, SubscriptionPlanNameEnum } from 'types/users.types';
import { excludeNumbers } from 'utils/helpers';
import {
  isSubscriptionFreePlan,
  isSubscriptionTrialPlan,
} from 'utils/subscriptions.utils';
import getSubscriptionConfig, {
  type PricingPlansData,
} from '../../../utils/getSubscriptionConfig';
import PlanWidget from '../PlanWidget';

const getPlansByInstallment = (plansIds: string[], installment: 1 | 12) => {
  return plansIds?.filter(
    (nickname: string) =>
      nickname === excludeNumbers(nickname).concat(String(installment)),
  );
};

const PricingPlans: FC<{
  plansData: PricingPlansData;
  installment: 1 | 12;
}> = ({ plansData, installment }) => {
  const currentPlanInternalName = useSubscriptionPlan()?.planInternalName;

  const detailedPlans = useGroupSubscriptionPlans(
    currentPlanInternalName as SubscriptionPlanNameEnum,
  ).data;

  const mainPlans = plansData?.options?.disableInstallmentFiltering
    ? plansData?.plans
    : getPlansByInstallment(plansData?.plans, installment);

  const extraPlans = plansData?.options?.disableInstallmentFiltering
    ? plansData?.extraPlans
    : getPlansByInstallment(plansData?.extraPlans, installment);

  return (
    <div className="flex space-x-6">
      {mainPlans
        ?.map(
          (planName: string) =>
            detailedPlans?.find(
              (plan) => plan.nickname === planName,
            ) as Partial<SubscriptionPlan>,
        )
        ?.map((plan: Partial<SubscriptionPlan> = {}) => (
          <PlanWidget
            plan={plan}
            key={plan.id}
            currentPlan={currentPlanInternalName as SubscriptionPlanNameEnum}
          />
        ))}

      {extraPlans?.length > 0 ? (
        <div className="w-[1px] bg-primary-300 my-5" />
      ) : null}

      {extraPlans
        ?.map(
          (planName: string) =>
            detailedPlans?.find(
              (plan) => plan.nickname === planName,
            ) as Partial<SubscriptionPlan>,
        )
        ?.map((plan: Partial<SubscriptionPlan> = {}) => (
          <PlanWidget
            plan={plan}
            key={plan.id}
            currentPlan={currentPlanInternalName as SubscriptionPlanNameEnum}
          />
        ))}
    </div>
  );
};

const PricingPlansWrapper: FC = () => {
  const { t } = useTranslation();

  const { data: user, isLoading: isLoadingUser } = useGetCurrentCustomerData();

  const currentPlanInternalName = useSubscriptionPlan()?.planInternalName;

  const { isLoading: isLoadingSubscriptionPlans } = useGroupSubscriptionPlans(
    currentPlanInternalName as SubscriptionPlanNameEnum,
  );

  const subscriptionConfig = useMemo(
    () =>
      getSubscriptionConfig(
        user as IUser,
        currentPlanInternalName as SubscriptionPlanNameEnum,
      ),
    [user, currentPlanInternalName],
  );

  const [installment, setInstallment] = useState(
    subscriptionConfig?.defaultInstallment,
  );

  const { mutate, isLoading: isRedirectingToCustomerPortal } =
    useRedirectToCustomerBillingPortal();

  const requiredCustomerFields = useFeatures(
    'revenue.required_customer_data_for_subscription',
  ) as string[];

  const isMissingCustomerData = requiredCustomerFields.some(
    (key) => !_get(user, key),
  );

  if (isLoadingUser || isLoadingSubscriptionPlans) return <CustomLoader />;

  return (
    <div>
      <div className="my-3 mx-2 mt-0 mb-6 flex items-start justify-between">
        <div className="max-w-[60%]">
          <div>
            <Title type={SizesEnum.h5} className="text-primary">
              <ReactMarkdown>
                {t(
                  subscriptionConfig?.disclaimer?.[
                    installment === 12 ? 'yearly' : 'monthly'
                  ],
                )}
              </ReactMarkdown>
            </Title>

            {!isSubscriptionFreePlan(currentPlanInternalName) &&
            !isSubscriptionTrialPlan(currentPlanInternalName) ? (
              <Button
                color="taxes"
                type="button"
                onClick={() => mutate()}
                className="mt-2"
                loading={isRedirectingToCustomerPortal}
              >
                {t('avatarmenu.billing')}
              </Button>
            ) : null}
          </div>

          {isMissingCustomerData ? (
            <ReactMarkdown
              className="w-[85%] mt-3 text-sm"
              components={{
                a: ({ children, href }) => (
                  <Link
                    href={href}
                    className="text-orange-500 hover:border-b-orange-500 font-medium"
                  >
                    {children}
                  </Link>
                ),
              }}
            >
              {t('subscriptions_screen.plans.missing_info_hint', {
                link: '/settings/personalDetails#company',
              })}
            </ReactMarkdown>
          ) : null}
        </div>

        <div>
          {subscriptionConfig?.options?.hideInstallmentToggle ? null : (
            <>
              <div className="bg-white rounded-full flex p-1">
                <button
                  type="button"
                  onClick={() => setInstallment(1)}
                  className={classNames('py-2 px-5 font-semibold', {
                    'bg-primary rounded-full text-white': installment === 1,
                  })}
                >
                  {t('subscriptions_screen.plans.monthly')}
                </button>
                <button
                  type="button"
                  className={classNames('py-2 px-5 font-semibold', {
                    'bg-primary rounded-full text-white': installment === 12,
                  })}
                  onClick={() => setInstallment(12)}
                >
                  {t('subscriptions_screen.plans.yearly')}
                </button>
              </div>
              <p className="text-right text-sm mt-2 font-semibold">
                {t('subscriptions_screen.plans.save_text')}
              </p>
            </>
          )}
        </div>
      </div>

      <PricingPlans plansData={subscriptionConfig} installment={installment} />
    </div>
  );
};

export default PricingPlansWrapper;
